// src/pages/OrderConfirmationPage.js

import React, { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle, Package, Truck, Home, ShoppingBag, Mail, Phone, AlertTriangle
} from 'lucide-react';
import StyledButton from '../components/ui/StyledButton';
import { useCart } from '../contexts/CartContext';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  // Prefer navigation state, else recover from sessionStorage
  const navState = location.state || {};
  const lastOrder = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('lastOrder');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Unified shape
  const orderId = navState.orderId || lastOrder?.orderNumber || null;
  const total = navState.total ?? lastOrder?.amount ?? null;
  const paymentMethod = navState.paymentMethod || lastOrder?.paymentMethod || 'PayPal';
  const success = Boolean(navState.success ?? lastOrder?.success ?? true);
  const orderData = navState.orderData || lastOrder || null;

  const formatPrice = (price) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(price || 0));

  // Vider automatiquement le panier après une commande confirmée
  useEffect(() => {
    if (success && (orderId || orderData)) {
      // Attendre un petit délai pour s'assurer que la page est bien chargée
      const timer = setTimeout(() => {
        clearCart();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [success, orderId, orderData, clearCart]);

  // Fallback if nothing is available
  if (!orderId && !orderData) {
    return (
      <div className="min-h-screen bg-gray-900 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 border border-gray-700 rounded-2xl p-8 text-center"
          >
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Impossible d’afficher la commande
            </h1>
            <p className="text-gray-300 mb-8">
              Nous n’avons pas trouvé les détails de votre commande sur cette page.
              Il est possible que vous soyez arrivé ici directement ou que la page ait été rechargée après le paiement.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <StyledButton onClick={() => navigate('/')} variant="primary" size="lg" icon={Home}>
                Retour à l’accueil
              </StyledButton>
              <StyledButton
                onClick={() => navigate('/orders')}
                variant="secondary"
                size="lg"
                icon={ShoppingBag}
              >
                Voir mes commandes
              </StyledButton>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">🎉 Commande confirmée !</h1>
          <p className="text-gray-300 text-lg">
            Merci pour votre commande. Votre paiement a été traité avec succès !
          </p>
          {success && (
            <div className="mt-4 p-4 bg-green-900/30 border border-green-700 rounded-lg">
              <p className="text-green-300 font-medium">
                ✅ Paiement réussi - Votre commande est en cours de traitement
              </p>
            </div>
          )}
        </motion.div>

        {/* Order Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800 rounded-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-white mb-6">Détails de votre commande</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2 text-iranian-400" />
                Informations de commande
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Numéro de commande:</span>
                  <span className="text-white font-mono">{orderId}</span>
                </div>
                {paymentMethod && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Méthode de paiement:</span>
                    <span className="text-white">{paymentMethod}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Date:</span>
                  <span className="text-white">
                    {orderData?.timestamp
                      ? new Date(orderData.timestamp).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : new Date().toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                  </span>
                </div>
                {orderData?.paymentReference && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Référence paiement:</span>
                    <span className="text-white font-mono text-sm">
                      {orderData.paymentReference}
                    </span>
                  </div>
                )}
                {orderData?.payerEmail && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Email PayPal:</span>
                    <span className="text-white">{orderData.payerEmail}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Total:</span>
                  <span className="text-iranian-400 font-semibold text-lg">
                    {total ? formatPrice(total) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                <Truck className="h-5 w-5 mr-2 text-iranian-400" />
                Livraison
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Statut:</span>
                  {/* Changed: En préparation → En attente (yellow) */}
                  <span className="text-yellow-400 font-medium">En attente</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Délai estimé:</span>
                  <span className="text-white">3-5 jours ouvrables</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Frais de livraison:</span>
                  <span className="text-green-400">Gratuits</span>
                </div>
                {orderData?.orderDetails && (
                  <>
                    {orderData.orderDetails.shipping_address && (
                      <div className="mt-4 pt-3 border-t border-gray-600">
                        <p className="text-gray-400 text-sm mb-2">Adresse de livraison:</p>
                        <p className="text-white text-sm">
                          {orderData.orderDetails.shipping_address}
                          {orderData.orderDetails.shipping_city &&
                            `, ${orderData.orderDetails.shipping_city}`}
                          {orderData.orderDetails.shipping_postal_code &&
                            ` ${orderData.orderDetails.shipping_postal_code}`}
                          {orderData.orderDetails.shipping_country &&
                            `, ${orderData.orderDetails.shipping_country}`}
                        </p>
                      </div>
                    )}
                    {orderData.orderDetails.first_name && orderData.orderDetails.last_name && (
                      <div>
                        <p className="text-gray-400 text-sm">Destinataire:</p>
                        <p className="text-white text-sm">
                          {orderData.orderDetails.first_name} {orderData.orderDetails.last_name}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Order Items */}
        {orderData?.orderItems?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800 rounded-xl p-8 mb-8"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Articles commandés</h2>
            <div className="space-y-4">
              {orderData.orderItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.08 }}
                  className="flex items-center justify-between p-4 bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <h3 className="text-white font-medium">{item.product_name}</h3>
                      <p className="text-gray-400 text-sm">SKU: {item.sku || 'N/A'}</p>
                      <p className="text-gray-400 text-sm">Quantité: {item.quantity}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">{formatPrice(item.total_price)}</p>
                    <p className="text-gray-400 text-sm">
                      {formatPrice(item.unit_price)} × {item.quantity}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gray-800 rounded-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-white mb-6">Besoin d'aide ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-iranian-400" />
              <div>
                <p className="text-white font-medium">Email</p>
                <p className="text-gray-400">support@mahja-jewelry.com</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-iranian-400" />
              <div>
                <p className="text-white font-medium">Téléphone</p>
                <p className="text-gray-400">+33 1 23 45 67 89</p>
              </div>
            </div>
          </div>
          <p className="text-gray-400 mt-4">
            Notre équipe est disponible du lundi au vendredi de 9h à 18h pour vous aider.
          </p>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <StyledButton onClick={() => navigate('/')} variant="primary" size="md" icon={Home}>
            Retour à l'accueil
          </StyledButton>

          <StyledButton
            onClick={() => navigate('/products')}
            variant="secondary"
            size="md"
            icon={ShoppingBag}
          >
            Continuer les achats
          </StyledButton>

          {/* Changed to StyledButton with secondary variant to match style */}
          <StyledButton
            onClick={() => navigate('/orders')}
            variant="secondary"
            size="md"
            icon={Package}
          >
            Voir mes commandes
          </StyledButton>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
